import { useState, useEffect } from "react";
import { format } from "date-fns";
import { zhCN } from "date-fns/locale";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Calendar as CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Label } from "@/components/ui/label";
import { PACKAGES_CN } from "@/types/booking-cn";

interface BookingFormCNProps {
  selectedPackageId?: string;
}

const BookingFormCN = ({ selectedPackageId }: BookingFormCNProps) => {
  const [packageId, setPackageId] = useState<string>(selectedPackageId || "");
  const [guests, setGuests] = useState<string>("");
  const [date, setDate] = useState<Date>();
  const [isProcessing, setIsProcessing] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  useEffect(() => {
    if (selectedPackageId) {
      setPackageId(selectedPackageId);
    }
  }, [selectedPackageId]);

  const selectedPackage = PACKAGES_CN.find(pkg => pkg.id === packageId);
  const totalPrice = selectedPackage ? selectedPackage.price * parseInt(guests || "1") : 0;

  const handleBookNow = async () => {
    if (!packageId || !guests || !date) {
      toast({
        title: "信息不完整",
        description: "请选择套餐、人数和出行日期",
        variant: "destructive"
      });
      return;
    }
    setIsProcessing(true);

    const createCartInBrowser = async (hosts: string[], paths: string[], payload: any) => {
      const defaultHosts = ["https://api.strabl.com", "https://sandbox.api.strabl.io", "https://sandbox.strabl.io", "https://checkout.strabl.io", "https://sandbox.checkout.strabl.io"];
      const defaultPaths = ["/v2/public/api/cart/", "/public/api/cart/", "/api/cart/", "/public/cart/"];
      const allHosts = Array.from(new Set([...(hosts || []), ...defaultHosts]));
      const allPaths = Array.from(new Set([...(paths || []), ...defaultPaths]));
      for (const base of allHosts) {
        for (const path of allPaths) {
          try {
            const url = `${base.replace(/\/+$/, "")}${path}`;
            const res = await fetch(url, {
              method: "POST",
              headers: {
                "Content-Type": "application/json"
              },
              body: JSON.stringify(payload)
            });
            if (res.ok) {
              const json = await res.json();
              const cartId = json?.data?.cartId || json?.cartId || json?.id;
              if (cartId) {
                const isSandbox = url.includes("sandbox");
                const checkoutBase = isSandbox ? "https://sandbox.checkout.strabl.io" : "https://checkout.strabl.io";
                window.location.href = `${checkoutBase}/?token=${cartId}`;
                return;
              }
            }
          } catch {
            // Network error - try next endpoint
          }
        }
      }
      toast({
        title: "支付错误",
        description: "无法连接支付网关，请重试。",
        variant: "destructive"
      });
      setIsProcessing(false);
    };

    try {
      const { data, error } = await supabase.functions.invoke("create-strabl-payment", {
        body: {
          amount: totalPrice,
          packageId: packageId,
          packageName: selectedPackage?.name,
          guests: parseInt(guests),
          date: format(date, "yyyy-MM-dd"),
          bookingType: "cn" // Chinese booking type
        }
      });

      if (error) throw error;
      
      if (data?.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      } else if (data?.manualCheckout && data?.hosts && data?.paths && data?.payload) {
        await createCartInBrowser(data.hosts, data.paths, data.payload);
      } else if (data?.bookingId) {
        toast({
          title: "预订已记录",
          description: data.message || "您的预订已成功保存。"
        });
        setIsProcessing(false);
      } else {
        throw new Error("No response received");
      }
    } catch {
      toast({
        title: "支付错误",
        description: "支付处理失败，请重试。",
        variant: "destructive"
      });
      setIsProcessing(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-2xl p-6 w-full max-w-md">
      <h2 className="text-xl font-bold mb-6 text-foreground">预订您的热气球之旅</h2>

      <div className="space-y-5">
        <div className="space-y-2">
          <Label htmlFor="package" className="text-base font-medium">
            选择套餐
          </Label>
          <Select value={packageId} onValueChange={setPackageId}>
            <SelectTrigger id="package" className="w-full h-12 bg-background">
              <SelectValue placeholder="请选择套餐" />
            </SelectTrigger>
            <SelectContent className="bg-popover z-50">
              {PACKAGES_CN.map(pkg => (
                <SelectItem key={pkg.id} value={pkg.id}>
                  {pkg.name} - AED {pkg.price}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="guests" className="text-base font-medium">
            人数
          </Label>
          <Select value={guests} onValueChange={setGuests}>
            <SelectTrigger id="guests" className="w-full h-12 bg-background">
              <SelectValue placeholder="选择人数" />
            </SelectTrigger>
            <SelectContent className="bg-popover z-50">
              {Array.from({ length: 24 }, (_, i) => i + 1).map(num => (
                <SelectItem key={num} value={num.toString()}>
                  {num} 位客人
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label className="text-base font-medium">出行日期</Label>
          <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full h-12 justify-start text-left font-normal bg-background",
                  !date && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-5 w-5" />
                {date ? format(date, "PPP", { locale: zhCN }) : "选择日期"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 bg-popover z-50" align="start">
              <Calendar
                mode="single"
                selected={date}
                onSelect={selectedDate => {
                  setDate(selectedDate);
                  setDatePickerOpen(false);
                }}
                disabled={date => date < new Date()}
                initialFocus
                className="pointer-events-auto"
              />
            </PopoverContent>
          </Popover>
        </div>

        {selectedPackage && guests && (
          <div className="bg-primary/5 rounded-lg p-4 space-y-2 border border-primary/10">
            <h3 className="font-semibold text-foreground mb-3">预订摘要</h3>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">套餐</span>
              <span className="font-medium">{selectedPackage.name}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">每人价格</span>
              <span className="font-medium">AED {selectedPackage.price}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">人数</span>
              <span className="font-medium">{guests}</span>
            </div>
            <div className="border-t border-primary/10 pt-2 mt-2">
              <div className="flex justify-between">
                <span className="font-semibold text-foreground">总价</span>
                <span className="font-bold text-primary text-lg">AED {totalPrice}</span>
              </div>
            </div>
          </div>
        )}

        <Button
          onClick={handleBookNow}
          disabled={!packageId || !guests || !date || isProcessing}
          className="w-full h-12 text-base font-semibold mt-6 bg-accent hover:bg-accent/90"
        >
          {isProcessing ? "处理中..." : "立即预订"}
        </Button>
      </div>
    </div>
  );
};

export default BookingFormCN;
